import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  Description,
  CheckCircle,
  Warning,
  Info,
  Gavel,
} from '@mui/icons-material';

const AuthorAgreement: React.FC = () => {
  const agreementConditions = [
    {
      title: 'Предоставление исключительных прав',
      description: 'Автор передает журналу исключительные права на использование произведения',
      icon: <Description />,
    },
    {
      title: 'Срок действия договора',
      description: 'Договор действует в течение всего срока действия авторского права',
      icon: <CheckCircle />,
    },
    {
      title: 'Территория действия',
      description: 'Права действуют на территории всего мира',
      icon: <Info />,
    },
    {
      title: 'Способы использования',
      description: 'Публикация, распространение, перевод, адаптация произведения',
      icon: <Gavel />,
    },
  ];

  const authorRights = [
    'Право на указание имени автора',
    'Право на защиту репутации автора',
    'Право на получение экземпляров журнала',
    'Право на использование в личных целях',
  ];

  const authorObligations = [
    'Гарантия оригинальности произведения',
    'Отсутствие нарушений авторских прав третьих лиц',
    'Соблюдение этических норм',
    'Предоставление достоверной информации',
  ];

  const publicationTerms = [
    'Срок рассмотрения: до 2 месяцев',
    'Срок публикации: до 6 месяцев после принятия',
    'Порядок внесения изменений',
    'Условия отзыва статьи',
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Авторский договор
      </Typography>
      
      <Typography variant="body1" paragraph>
        При публикации статьи в журнале между автором и редакцией заключается авторский договор, 
        который регулирует права и обязанности сторон.
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        Договор заключается в электронном виде при подаче статьи и является обязательным 
        для всех авторов.
      </Alert>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Условия договора
        </Typography>
        <List>
          {agreementConditions.map((condition, index) => (
            <ListItem key={index}>
              <ListItemIcon>{condition.icon}</ListItemIcon>
              <ListItemText
                primary={condition.title}
                secondary={condition.description}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Права автора
        </Typography>
        <Typography variant="body1" paragraph>
          Автор сохраняет определенные права на свое произведение даже после передачи 
          исключительных прав журналу.
        </Typography>
        <List>
          {authorRights.map((right, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary={right} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Обязанности автора
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Несоблюдение обязанностей может привести к отказу в публикации или отзыву статьи.
        </Alert>
        <List>
          {authorObligations.map((obligation, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText primary={obligation} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Сроки публикации
        </Typography>
        <Typography variant="body1" paragraph>
          Договором устанавливаются конкретные сроки для различных этапов публикации.
        </Typography>
        <List>
          {publicationTerms.map((term, index) => (
            <ListItem key={index}>
              <ListItemText primary={term} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Подписание договора
        </Typography>
        <Typography variant="body1" paragraph>
          Договор подписывается в электронном виде при подаче статьи через систему журнала.
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Процесс подписания:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="1. Заполнение формы при подаче статьи" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Ознакомление с условиями договора" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Электронная подпись всех соавторов" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. Подтверждение от редакции" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />
        
        <Alert severity="success">
          <Typography variant="body2">
            <strong>Важно:</strong> Договор вступает в силу с момента принятия статьи к публикации 
            и действует в течение всего срока действия авторского права.
          </Typography>
        </Alert>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default AuthorAgreement;
